/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IGroup,
    IGroups,
    IOrderConfirmationViewProps,
    IOrderInformation,
    ISalesLine,
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management';
import { IOrderConfirmationProps as IOrderConfirmationExtensionProps } from '../definition-extensions/order-confirmation.ext.props.autogenerated';

import { OrderShipments } from '@msdyn365-commerce/retail-proxy/dist/index';

import { ICheckoutViewProps } from '@msdyn365-commerce-modules/checkout/src/modules/checkout';

import { getLineItems } from './custom-components/get-line-items';

import { ICheckoutProps } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/checkout.props.autogenerated';
import { ICheckoutData } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/checkout.data';

export interface IState {
    orderTotal: string;
    contactDetails: string;
}

export interface IOrderPlan {
    orderSummary: IOrderSummary | undefined;
    salesOrder: OrderShipments | undefined;
    salesLines: ISalesLine[] | undefined;
}
/**
 *
 * Order Confirmation Component
 * @extends {React.PureComponent<IOrderConfirmationViewProps & IOrderConfirmationExtensionProps<{}> & ICheckoutViewProps & ICheckoutProps<ICheckoutData>>}
 */

export class OrderConfirmationView extends React.PureComponent<
    IOrderConfirmationViewProps & IOrderConfirmationExtensionProps<{}> & ICheckoutViewProps & ICheckoutProps<ICheckoutData>,
    IState
> {
    constructor(
        props: IOrderConfirmationViewProps & IOrderConfirmationExtensionProps<{}> & ICheckoutViewProps & ICheckoutProps<ICheckoutData>
    ) {
        super(props);
        this.state = {
            orderTotal: '',
            contactDetails: ''
        };
    }
    public render(): JSX.Element {
        const {
            moduleProps,
            orderInfomation,
            salesOrder,
            orderSummary,
            payment,
            salesLines,
            help,
            groups,
            config: { orderDetailsLabel }
        } = this.props;
        const lineItemsUpdated = getLineItems(this.props);
        let { lineItems } = this.props;
        lineItems;
        lineItems = lineItemsUpdated;
        const orderPlan: IOrderPlan = {
            orderSummary,
            salesOrder,
            salesLines
        };
        return (
            <Module {...moduleProps}>
                {this.renderHeading()}
                {orderInfomation && <this.OrderConfirmationOrderInfomation {...orderInfomation} />}
                {orderPlan && <this.renderOrderDeliveryPlan {...orderPlan} />}
                <div className='ms-order-confirmation__order-heading'>{orderDetailsLabel}</div>
                {groups && <this.OrderConfirmationGroups {...groups} />}
                {orderSummary && <this.PaymentDetails />}
                {payment && <this.OrderConfirmationPayment {...payment} />}
                <span />
                {this.renderContactDetails()}
                {this.renderLinks()}
                <span />
                {help && <this.OrderConfirmationHelp {...help} />}
                {this.renderHomePageLink()}
            </Module>
        );
    }
    public OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title }) => {
        const {
            data: {
                orderHydration: { result }
            }
        } = this.props;
        let cardType = result && result.salesOrder && result.salesOrder.TenderLines && result.salesOrder.TenderLines[0].CardTypeId;
        cardType = cardType && cardType.toLowerCase();
        const cardIconClass =
            cardType &&
            (cardType.includes('visa')
                ? 'visa-card-icon'
                : cardType.includes('master')
                ? 'master-card-icon'
                : cardType.includes('amex')
                ? 'american-exp-icon'
                : '');
        return (
            <Node {...paymentMethodsProps}>
                <h3>{title}</h3>
                <div className='row'>
                    <div className='msc-payment-method__card-image'>
                        <span className={`card-icon ${cardIconClass}`} />
                    </div>
                </div>
            </Node>
        );
    };
    public renderOrderDeliveryPlan: React.FC<IOrderPlan> = orderPlan => {
        const { orderConfirmationNumberLabel, orderTotalLabel } = this.props.config;
        const {
            context: { cultureFormatter }
        } = this.props;
        const { salesOrder, salesLines } = orderPlan;
        const _shippingAddress = salesLines && salesLines[0] && salesLines[0].data && salesLines[0].data.salesLine.ShippingAddress;
        const _total: string = salesOrder && salesOrder.TotalAmount ? salesOrder.TotalAmount.toFixed(2) : '';
        const _name: string = _shippingAddress && _shippingAddress.Name ? _shippingAddress.Name : '';
        const _address: string = _shippingAddress && _shippingAddress.FullAddress ? _shippingAddress.FullAddress : '';
        this.setState({
            orderTotal: cultureFormatter.formatCurrency(_total),
            contactDetails: _name.concat(', ').concat(_address)
        });
        const orderID: string = salesOrder && salesOrder.ChannelReferenceId ? salesOrder.ChannelReferenceId : '';
        return (
            <div className='ms-order-delivery-wrap'>
                <p className='row'>
                    {<span className='col-md-6 ms-order-delivery-plan__label'>{orderConfirmationNumberLabel}</span>}
                    <span className='col-md-6 ms-order-delivery__value'>
                        {' '}
                        <strong>{orderID}</strong>
                    </span>
                </p>
                <p className='row'>
                    {<span className='col-md-6 ms-order-delivery-plan__label'>{orderTotalLabel}</span>}
                    <span className='col-md-6 ms-order-delivery__value'>
                        {' '}
                        <strong>{cultureFormatter.formatCurrency(_total)}</strong>
                    </span>
                </p>
            </div>
        );
    };
    public renderHomePageLink = () => {
        const {
            config: { backToHomePageLabel },
            app: {
                routes: { home }
            }
        } = this.props;
        return (
            backToHomePageLabel &&
            home.destinationUrl && (
                <div className='ms-order-confirmation__homepage-link' key='homepage-links'>
                    <a href={home.destinationUrl}>{backToHomePageLabel}</a>
                </div>
            )
        );
    };
    public renderLinks = () => {
        const { returnPolicyLink } = this.props.config;
        return (
            returnPolicyLink &&
            returnPolicyLink.linkText && (
                <div className='ms-order-confirmation__links' key='links'>
                    <a target='_blank' href={returnPolicyLink.linkUrl.destinationUrl}>
                        <u>{returnPolicyLink.linkText}</u>
                    </a>
                </div>
            )
        );
    };
    private renderContactDetails = () => {
        const {
            config: { contactDetailsLabel }
        } = this.props;
        return (
            <div className='ms-order-confirmation__contact-details' key='contact'>
                <h3>{contactDetailsLabel}</h3>
                <span>{this.state.contactDetails}</span>
            </div>
        );
    };
    private renderHeading = () => {
        const {
            config: { thankYouNote }
        } = this.props;
        return <h1 className='ms-order-confirmation__heading'>{thankYouNote}</h1>;
    };
    private OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({ orderInformationProps }) => {
        const {
            config: { emailText }
        } = this.props;
        const receiptEmailID = this.props.data.orderHydration.result && this.props.data.orderHydration.result.salesOrder.ReceiptEmail;
        return (
            <Node {...orderInformationProps}>
                <p>
                    {emailText}
                    <strong>{receiptEmailID}</strong>
                </p>
            </Node>
        );
    };
    private OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine }) => (
        <Node {...salesLineProps}>{salesLine}</Node>
    );
    private OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, salesLinesProps, salesLines }) => (
        <Node {...groupProps}>
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map(salesLine => (
                        <this.OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                    ))}
                </Node>
            )}
        </Node>
    );
    private OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
        <Node {...groupsProps}>
            {groups.map((group, index) => (
                <this.OrderConfirmationGroup key={index} {...group} />
            ))}
        </Node>
    );

    private PaymentDetails: React.FC = () => {
        const {
            config: { priceTotalLabel, deliveryTotalLabel, totalLabel, paymentDetailsLabel },
            context: { cultureFormatter }
        } = this.props;
        const totalExlDelivery = this.props.data.orderHydration.result && this.props.data.orderHydration.result.salesOrder.SubtotalAmount;
        const deliveryCharges = this.props.data.orderHydration.result && this.props.data.orderHydration.result.salesOrder.ChargeAmount;
        const totalCharges = this.props.data.orderHydration.result && this.props.data.orderHydration.result.salesOrder.GrossAmount;
        return (
            <div className='ms-order-confirmation__promotion' key='promotion-details'>
                {paymentDetailsLabel && <h3>{paymentDetailsLabel}</h3>}
                <p className='ms-order-confirmation__order-summary-line-subtotal'>
                    <span className='ms-order-confirmation__order-summary-label'>{priceTotalLabel}</span>
                    <span className='msc-price ms-order-confirmation__order-summary-price'>
                        {totalExlDelivery && (
                            <span className='msc-price__actual'>{cultureFormatter.formatCurrency(totalExlDelivery.toFixed(2))}</span>
                        )}
                    </span>
                </p>
                <p className='ms-order-confirmation__order-summary-line-shipping'>
                    <span className='ms-order-confirmation__order-summary-label'>{deliveryTotalLabel}</span>
                    <span className='msc-price ms-order-confirmation__order-summary-price'>
                        {deliveryCharges && (
                            <span className='msc-price__actual'>{cultureFormatter.formatCurrency(deliveryCharges.toFixed(2))}</span>
                        )}
                    </span>
                </p>
                <p className='ms-order-confirmation__order-summary-line-total'>
                    <span className='ms-order-confirmation__order-summary-label'>{totalLabel}</span>
                    <span className='msc-price ms-order-confirmation__order-summary-price'>
                        {totalCharges && (
                            <span className='msc-price__actual'>{cultureFormatter.formatCurrency(totalCharges.toFixed(2))}</span>
                        )}
                    </span>
                </p>
            </div>
        );
    };
    private OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
        <Node {...helpProps}>
            {needHelpLabel}
            {helpLineNumberLabel}
            {contactNumber}
        </Node>
    );
}

export default OrderConfirmationView;
